/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {
  ReportLegalTrackEnum,
  ReportPriorityGroupEnum,
  ReportReasonEnum,
  ReportTargetKindEnum,
} from '@repo/database'

export const REPORT_PRIORITY_POLICY_VERSION = '2026-09-16.1'

export const LEAF_TARGET_KINDS = [
  'PROFILE',
  'MEDIA',
  'ATTACHED_MEDIA',
  'POST',
  'COMMENT',
  'MESSAGE',
  'REVIEW',
  'PLACE',
  'PUBLICATION',
  'EVENT',
  'GROUP',
  'VENDOR_MARKET',
  'RENDEZVOUS',
] as const satisfies readonly ReportTargetKindEnum[]

export const ALL_TARGET_KINDS = [
  ...LEAF_TARGET_KINDS,
  'CONVERSATION',
] as const satisfies readonly ReportTargetKindEnum[]

export const REASON_UI_GROUPS = ['SAFETY', 'HARM', 'INTEGRITY', 'LISTING', 'OTHER'] as const

export type ReportReasonUiGroup = (typeof REASON_UI_GROUPS)[number]

export type ReportReasonCatalogEntry = {
  reason: ReportReasonEnum
  defaultTrack: ReportLegalTrackEnum
  defaultPriority: ReportPriorityGroupEnum
  allowedTargetKinds: readonly ReportTargetKindEnum[]
  uiGroup: ReportReasonUiGroup
  showReporterIsDepicted: boolean
  showImmediateDanger: boolean
  requiresCopyrightDeclaration: boolean
  requiresWorkAndLocation: boolean
  defaultNotifyReporter: boolean
}

const ALL = LEAF_TARGET_KINDS

const MEDIA_LIKENESS_KINDS: readonly ReportTargetKindEnum[] = [
  'MEDIA',
  'ATTACHED_MEDIA',
  'POST',
  'PROFILE',
  'MESSAGE',
  'COMMENT',
]

const REAL_DEATH_KINDS: readonly ReportTargetKindEnum[] = ['MEDIA', 'ATTACHED_MEDIA', 'POST', 'MESSAGE']

const IMPERSONATION_KINDS: readonly ReportTargetKindEnum[] = ['PROFILE', 'MESSAGE', 'POST', 'COMMENT']

const COPYRIGHT_KINDS: readonly ReportTargetKindEnum[] = ['MEDIA', 'ATTACHED_MEDIA', 'POST', 'PUBLICATION', 'COMMENT']

const OWNERSHIP_KINDS: readonly ReportTargetKindEnum[] = ['EVENT', 'PLACE', 'GROUP', 'VENDOR_MARKET', 'PUBLICATION']

const NO_LONGER_EXISTS_KINDS: readonly ReportTargetKindEnum[] = ['PLACE', 'VENDOR_MARKET', 'EVENT', 'GROUP']

const LISTING_VIOLATION_KINDS: readonly ReportTargetKindEnum[] = [
  'EVENT',
  'PLACE',
  'GROUP',
  'PUBLICATION',
  'VENDOR_MARKET',
  'RENDEZVOUS',
]

const NSFW_KINDS: readonly ReportTargetKindEnum[] = ['MEDIA', 'ATTACHED_MEDIA']

export const REPORT_REASON_CATALOG: readonly ReportReasonCatalogEntry[] = [
  {
    reason: 'CHILD_SEXUAL_ABUSE_MATERIAL',
    defaultTrack: 'CHILD_SAFETY',
    defaultPriority: 'CRITICAL',
    allowedTargetKinds: ALL,
    uiGroup: 'SAFETY',
    showReporterIsDepicted: false,
    showImmediateDanger: true,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'APPARENT_MINOR',
    defaultTrack: 'CHILD_SAFETY',
    defaultPriority: 'CRITICAL',
    allowedTargetKinds: ALL,
    uiGroup: 'SAFETY',
    showReporterIsDepicted: false,
    showImmediateDanger: true,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'NON_CONSENSUAL_INTIMATE_IMAGERY',
    defaultTrack: 'NCII',
    defaultPriority: 'CONSENT_LEGAL',
    allowedTargetKinds: ALL,
    uiGroup: 'SAFETY',
    showReporterIsDepicted: true,
    showImmediateDanger: true,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'UNAUTHORIZED_SYNTHETIC_LIKENESS',
    defaultTrack: 'NCII',
    defaultPriority: 'CONSENT_LEGAL',
    allowedTargetKinds: MEDIA_LIKENESS_KINDS,
    uiGroup: 'SAFETY',
    showReporterIsDepicted: true,
    showImmediateDanger: true,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'SEX_TRAFFICKING',
    defaultTrack: 'TRAFFICKING',
    defaultPriority: 'CONSENT_LEGAL',
    allowedTargetKinds: ALL,
    uiGroup: 'SAFETY',
    showReporterIsDepicted: false,
    showImmediateDanger: true,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'COMMERCIAL_SEX',
    defaultTrack: 'TRAFFICKING',
    defaultPriority: 'CONSENT_LEGAL',
    allowedTargetKinds: ALL,
    uiGroup: 'SAFETY',
    showReporterIsDepicted: false,
    showImmediateDanger: true,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'ANIMAL_ABUSE',
    defaultTrack: 'OTHER_ILLEGAL',
    defaultPriority: 'CONSENT_LEGAL',
    allowedTargetKinds: ALL,
    uiGroup: 'SAFETY',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'REAL_DEATH_OR_SNUFF',
    defaultTrack: 'OTHER_ILLEGAL',
    defaultPriority: 'CONSENT_LEGAL',
    allowedTargetKinds: REAL_DEATH_KINDS,
    uiGroup: 'SAFETY',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'OTHER_ILLEGAL',
    defaultTrack: 'OTHER_ILLEGAL',
    defaultPriority: 'CONSENT_LEGAL',
    allowedTargetKinds: ALL,
    uiGroup: 'SAFETY',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'PERSONAL_ATTACK_OR_THREAT',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'HARM',
    allowedTargetKinds: ALL,
    uiGroup: 'HARM',
    showReporterIsDepicted: false,
    showImmediateDanger: true,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'HARASSMENT_OR_BULLYING',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'HARM',
    allowedTargetKinds: ALL,
    uiGroup: 'HARM',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'HATE_SPEECH',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'HARM',
    allowedTargetKinds: ALL,
    uiGroup: 'HARM',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'STALKING_OR_DOXXING',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'HARM',
    allowedTargetKinds: ALL,
    uiGroup: 'HARM',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'IMPERSONATION',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'HARM',
    allowedTargetKinds: IMPERSONATION_KINDS,
    uiGroup: 'HARM',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'PERSONAL_INFORMATION',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'HARM',
    allowedTargetKinds: ALL,
    uiGroup: 'HARM',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: true,
  },
  {
    reason: 'SPAM',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'INTEGRITY',
    allowedTargetKinds: ALL,
    uiGroup: 'INTEGRITY',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
  {
    reason: 'FAKE_PROFILE',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'INTEGRITY',
    allowedTargetKinds: ['PROFILE'],
    uiGroup: 'INTEGRITY',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
  {
    reason: 'COPYRIGHT',
    defaultTrack: 'COPYRIGHT',
    defaultPriority: 'INTEGRITY',
    allowedTargetKinds: COPYRIGHT_KINDS,
    uiGroup: 'INTEGRITY',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: true,
    requiresWorkAndLocation: true,
    defaultNotifyReporter: true,
  },
  {
    reason: 'NOT_OWNER',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'INTEGRITY',
    allowedTargetKinds: OWNERSHIP_KINDS,
    uiGroup: 'INTEGRITY',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
  {
    reason: 'REQUEST_OWNER_TRANSFER',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'INTEGRITY',
    allowedTargetKinds: OWNERSHIP_KINDS,
    uiGroup: 'INTEGRITY',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
  {
    reason: 'BAN_EVASION',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'INTEGRITY',
    allowedTargetKinds: ['PROFILE'],
    uiGroup: 'INTEGRITY',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
  {
    reason: 'NO_LONGER_EXISTS',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'CONTENT_QUALITY',
    allowedTargetKinds: NO_LONGER_EXISTS_KINDS,
    uiGroup: 'LISTING',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
  {
    reason: 'NOT_EVENT',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'CONTENT_QUALITY',
    allowedTargetKinds: ['EVENT'],
    uiGroup: 'LISTING',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
  {
    reason: 'EVENT_MISREPRESENTATION',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'CONTENT_QUALITY',
    allowedTargetKinds: ['EVENT'],
    uiGroup: 'LISTING',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
  {
    reason: 'AUP_LISTING_VIOLATION',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'CONTENT_QUALITY',
    allowedTargetKinds: LISTING_VIOLATION_KINDS,
    uiGroup: 'LISTING',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
  {
    reason: 'NSFW_INCORRECT',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'CONTENT_QUALITY',
    allowedTargetKinds: NSFW_KINDS,
    uiGroup: 'LISTING',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
  {
    reason: 'OTHER',
    defaultTrack: 'COMMUNITY',
    defaultPriority: 'CONTENT_QUALITY',
    allowedTargetKinds: ALL,
    uiGroup: 'OTHER',
    showReporterIsDepicted: false,
    showImmediateDanger: false,
    requiresCopyrightDeclaration: false,
    requiresWorkAndLocation: false,
    defaultNotifyReporter: false,
  },
]

const CATALOG_BY_REASON = new Map(REPORT_REASON_CATALOG.map((entry) => [entry.reason, entry]))

export const REPORT_REASON_VALUES = REPORT_REASON_CATALOG.map((entry) => entry.reason) as [
  ReportReasonEnum,
  ...ReportReasonEnum[],
]

export const REPORT_LEGAL_TRACK_VALUES = [
  'CHILD_SAFETY',
  'NCII',
  'TRAFFICKING',
  'OTHER_ILLEGAL',
  'COPYRIGHT',
  'COMMUNITY',
] as const satisfies readonly ReportLegalTrackEnum[]

export const ADMIN_REPORT_CASE_VIEWS = [
  'UNRESOLVED',
  'ESCALATED',
  'AWAITING_THIRD_PARTY',
  'SLA_OVERDUE',
  'RESOLVED',
] as const

export const PRIORITY_RANK: Record<ReportPriorityGroupEnum, number> = {
  CRITICAL: 0,
  CONSENT_LEGAL: 1,
  HARM: 2,
  INTEGRITY: 3,
  CONTENT_QUALITY: 4,
}

export const TRACK_SEVERITY_RANK: Record<ReportLegalTrackEnum, number> = {
  CHILD_SAFETY: 0,
  NCII: 1,
  TRAFFICKING: 2,
  OTHER_ILLEGAL: 3,
  COPYRIGHT: 4,
  COMMUNITY: 5,
}

export const SLA_LEGAL_TRACKS: ReadonlySet<ReportLegalTrackEnum> = new Set([
  'CHILD_SAFETY',
  'NCII',
  'TRAFFICKING',
  'OTHER_ILLEGAL',
])

export const AUTO_RESTRICT_TRACKS: ReadonlySet<ReportLegalTrackEnum> = new Set(['CHILD_SAFETY', 'NCII', 'TRAFFICKING'])

export const RATE_LIMIT_BYPASS_TRACKS: ReadonlySet<ReportLegalTrackEnum> = new Set([
  'CHILD_SAFETY',
  'NCII',
  'TRAFFICKING',
])

export const SELF_REPORT_BLOCKED_REASONS: ReadonlySet<ReportReasonEnum> = new Set(['FAKE_PROFILE', 'BAN_EVASION'])

export const MEDIA_OR_POST_KINDS: ReadonlySet<ReportTargetKindEnum> = new Set(['MEDIA', 'ATTACHED_MEDIA', 'POST'])

export const DETAILS_MAX_LENGTH = 4000
export const EVIDENCE_SNAPSHOT_MAX_BYTES = 16 * 1024
export const TEXT_EXCERPT_MAX_LENGTH = 500

export const REPORTS_PER_HOUR_CAP = 10
export const REPORTS_PER_DAY_CAP = 30
export const SAFETY_REPORTS_PER_DAY_CAP = 60

export const UNRESOLVED_CASE_STATUSES = [
  'OPEN',
  'IN_REVIEW',
  'ACTION_PENDING',
  'ESCALATED',
  'AWAITING_THIRD_PARTY',
  'APPEAL_OPEN',
] as const

export function getReasonCatalogEntry(reason: ReportReasonEnum): ReportReasonCatalogEntry | undefined {
  return CATALOG_BY_REASON.get(reason)
}

export function mostSevereReason(reasons: readonly ReportReasonEnum[]): ReportReasonEnum | undefined {
  let best: ReportReasonCatalogEntry | undefined
  for (const reason of reasons) {
    const entry = CATALOG_BY_REASON.get(reason)
    if (!entry) {
      continue
    }
    if (!best) {
      best = entry
      continue
    }
    if (TRACK_SEVERITY_RANK[entry.defaultTrack] < TRACK_SEVERITY_RANK[best.defaultTrack]) {
      best = entry
      continue
    }
    if (
      entry.defaultTrack === best.defaultTrack &&
      PRIORITY_RANK[entry.defaultPriority] < PRIORITY_RANK[best.defaultPriority]
    ) {
      best = entry
    }
  }
  return best?.reason
}

export function defaultNotifyReporterForReasons(reasons: readonly ReportReasonEnum[]): boolean {
  const reason = mostSevereReason(reasons)
  if (!reason) {
    return true
  }
  return CATALOG_BY_REASON.get(reason)?.defaultNotifyReporter ?? true
}

export function isReasonAllowedForTarget(reason: ReportReasonEnum, targetKind: ReportTargetKindEnum): boolean {
  const entry = CATALOG_BY_REASON.get(reason)
  if (!entry) {
    return false
  }
  return entry.allowedTargetKinds.includes(targetKind)
}

export function reasonsForTargetKind(targetKind: ReportTargetKindEnum): ReportReasonCatalogEntry[] {
  return REPORT_REASON_CATALOG.filter((entry) => entry.allowedTargetKinds.includes(targetKind))
}

export function moreSeverePriority(a: ReportPriorityGroupEnum, b: ReportPriorityGroupEnum): ReportPriorityGroupEnum {
  return PRIORITY_RANK[a] <= PRIORITY_RANK[b] ? a : b
}

export function moreSevereTrack(a: ReportLegalTrackEnum, b: ReportLegalTrackEnum): ReportLegalTrackEnum {
  return TRACK_SEVERITY_RANK[a] <= TRACK_SEVERITY_RANK[b] ? a : b
}

export function effectivePriorityGroup(params: {
  priorityGroup: ReportPriorityGroupEnum
  priorityOverride?: ReportPriorityGroupEnum | null
}): ReportPriorityGroupEnum {
  return params.priorityOverride ?? params.priorityGroup
}

export function shouldShowReporterIsDepicted(reason: ReportReasonEnum, targetKind: ReportTargetKindEnum): boolean {
  const entry = CATALOG_BY_REASON.get(reason)
  if (!entry) {
    return false
  }
  return entry.showReporterIsDepicted || MEDIA_OR_POST_KINDS.has(targetKind)
}

export const ALLOWED_PARENT_COMBINATIONS: ReadonlyArray<{
  leaf: ReportTargetKindEnum
  parent: ReportTargetKindEnum
}> = [
  { leaf: 'MESSAGE', parent: 'CONVERSATION' },
  { leaf: 'MEDIA', parent: 'CONVERSATION' },
  { leaf: 'COMMENT', parent: 'POST' },
  { leaf: 'COMMENT', parent: 'MEDIA' },
  { leaf: 'COMMENT', parent: 'PROFILE' },
  { leaf: 'REVIEW', parent: 'PLACE' },
  { leaf: 'REVIEW', parent: 'PUBLICATION' },
  { leaf: 'REVIEW', parent: 'VENDOR_MARKET' },
  { leaf: 'ATTACHED_MEDIA', parent: 'PLACE' },
  { leaf: 'ATTACHED_MEDIA', parent: 'PUBLICATION' },
  { leaf: 'ATTACHED_MEDIA', parent: 'EVENT' },
  { leaf: 'ATTACHED_MEDIA', parent: 'GROUP' },
  { leaf: 'ATTACHED_MEDIA', parent: 'VENDOR_MARKET' },
]

export function isParentCombinationAllowed(
  leaf: ReportTargetKindEnum,
  parent: ReportTargetKindEnum | null | undefined
): boolean {
  if (!parent) {
    return true
  }
  return ALLOWED_PARENT_COMBINATIONS.some((combo) => combo.leaf === leaf && combo.parent === parent)
}
